import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import api from '../utils/api';

const Contact = () => {
  const { t, language } = useLanguage();
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, settingsRes] = await Promise.all([
          api.get('/pages/iletisim'),
          api.get('/settings')
        ]);
        setPage(pageRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const title = page ? (language === 'tr' ? page.title_tr : page.title_en) : t.nav.contact;

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="contact-page">
      <SEO 
        title="İletişim"
        description="BrightMind Software ile iletişime geçin. Projeleriniz için bizimle konuşun."
        keywords="iletişim, BrightMind Software, proje teklifi, yazılım danışmanlığı"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-bms-light via-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-bms-text-light hover:text-bms-primary mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.pages.backToHome}
              </Link>
              <h1 className="text-3xl md:text-5xl font-bold text-bms-text mb-4">
                {title}
              </h1>
              <p className="text-lg text-bms-text-light max-w-2xl mx-auto">
                {t.contact.subtitle}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <ScrollReveal>
            <div>
              <h2 className="text-2xl font-bold text-bms-text mb-6">İletişim Bilgileri</h2>
              
              <div className="space-y-6">
                {settings?.contact_email && (
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-corporate">
                    <div className="icon-container">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-bms-text mb-1">E-posta</h3>
                      <a href={`mailto:${settings.contact_email}`} className="text-bms-primary hover:underline">
                        {settings.contact_email}
                      </a>
                    </div>
                  </div>
                )}
                
                {settings?.contact_phone && (
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-corporate">
                    <div className="icon-container">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-bms-text mb-1">Telefon</h3>
                      <a href={`tel:${settings.contact_phone}`} className="text-bms-primary hover:underline">
                        {settings.contact_phone}
                      </a>
                    </div>
                  </div>
                )}
                
                {(settings?.contact_address_tr || settings?.contact_address_en) && (
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-corporate">
                    <div className="icon-container">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-bms-text mb-1">{language === 'tr' ? 'Adres' : 'Address'}</h3>
                      <p className="text-bms-text-light">{language === 'tr' ? settings.contact_address_tr : settings.contact_address_en}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>

          {/* Contact Form */}
          <ScrollReveal delay={0.2}>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-corporate">
              <h2 className="text-2xl font-bold text-bms-text mb-6">{t.contact.formTitle}</h2>
              
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-bms-text mb-2">{t.contact.successTitle}</h3>
                  <p className="text-bms-text-light">{t.contact.successMessage}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-bms-text mb-2">
                      {t.contact.name}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                      placeholder={t.contact.namePlaceholder}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-bms-text mb-2">
                      {t.contact.email}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                      placeholder={t.contact.emailPlaceholder}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-bms-text mb-2">
                      {t.contact.message}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all resize-none"
                      placeholder={t.contact.messagePlaceholder}
                    />
                  </div>
                  
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg font-semibold transition-all"
                  >
                    <Send className="w-5 h-5" />
                    {t.contact.send}
                  </motion.button>
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
};

export default Contact;
